import { useEffect, useState } from "react";
import { api, AI_CATEGORIES } from "../lib/api";
import { toast } from "sonner";
import { Search, RefreshCw, Eye, EyeOff, Tag, X, Sparkles } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

const SCOPES = [
  { key: "priority", label: "Prioritaires" },
  { key: "all", label: "Tous" },
  { key: "hidden", label: "Ignorés" },
];
const PERIODS = [
  { key: "today", label: "Aujourd'hui" },
  { key: "week", label: "Semaine" },
  { key: "all", label: "Tout" },
];

export default function Emails() {
  const [scope, setScope] = useState("priority");
  const [period, setPeriod] = useState("all");
  const [search, setSearch] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [tagInput, setTagInput] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/emails", {
        params: { scope, period, search: search || undefined, unread_only: unreadOnly, limit: 200 },
      });
      setEmails(r.data);
    } catch (err) {
      toast.error("Erreur de chargement");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, period, unreadOnly]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/emails/sync", null, { params: { limit: 50 } });
      toast.success(`${r.data.new} nouveaux • ${r.data.classified} classifiés IA`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec sync");
    } finally { setSyncing(false); }
  };

  const openEmail = async (em) => {
    try {
      const r = await api.get(`/emails/${em.id}`);
      setSelected(r.data);
      if (!em.is_read) {
        await api.post(`/emails/${em.id}/read`);
        setEmails((prev) => prev.map(e => e.id === em.id ? { ...e, is_read: true } : e));
      }
    } catch (err) { toast.error("Erreur"); }
  };

  const updateSelected = (patch) => setSelected((s) => ({ ...s, ...patch }));

  const addTag = async () => {
    if (!tagInput.trim() || !selected) return;
    const nextTags = Array.from(new Set([...(selected.tags || []), tagInput.trim().toLowerCase()]));
    try {
      await api.put(`/emails/${selected.id}/tags`, { tags: nextTags });
      updateSelected({ tags: nextTags });
      setEmails((prev) => prev.map(e => e.id === selected.id ? { ...e, tags: nextTags } : e));
      setTagInput("");
    } catch (err) { toast.error("Erreur tags"); }
  };

  const removeTag = async (tag) => {
    if (!selected) return;
    const nextTags = (selected.tags || []).filter(t => t !== tag);
    try {
      await api.put(`/emails/${selected.id}/tags`, { tags: nextTags });
      updateSelected({ tags: nextTags });
      setEmails((prev) => prev.map(e => e.id === selected.id ? { ...e, tags: nextTags } : e));
    } catch (err) { toast.error("Erreur tags"); }
  };

  const correctCategory = async (category) => {
    if (!selected) return;
    try {
      await api.post(`/emails/${selected.id}/category`, { category });
      updateSelected({ ai_category: category, user_category: category });
      setEmails((prev) => prev.map(e => e.id === selected.id ? { ...e, ai_category: category, user_category: category } : e));
      toast.success(`Catégorie → ${category}. L'IA apprend de cette correction.`);
    } catch (err) { toast.error("Erreur"); }
  };

  return (
    <div className="p-6 md:p-8" data-testid="emails-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline mb-1">// Inbox</div>
          <h1 className="text-3xl font-bold tracking-tight" style={{fontFamily:"'Work Sans', sans-serif"}}>Emails</h1>
        </div>
        <button onClick={sync} disabled={syncing} data-testid="emails-sync-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-[#002FA7] hover:bg-[#002277] text-white text-sm font-medium transition-colors disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
          Synchroniser IMAP
        </button>
      </div>

      <div className="bg-white border border-gray-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex border border-gray-300" data-testid="scope-tabs">
          {SCOPES.map((s) => (
            <button key={s.key} data-testid={`scope-${s.key}`}
              onClick={() => setScope(s.key)}
              className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wider transition-colors ${
                scope === s.key ? "bg-[#002FA7] text-white" : "bg-white text-gray-700 hover:bg-gray-50"
              }`}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex border border-gray-300">
          {PERIODS.map((p) => (
            <button key={p.key} data-testid={`period-${p.key}`}
              onClick={() => setPeriod(p.key)}
              className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wider transition-colors ${
                period === p.key ? "bg-gray-900 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
              }`}>
              {p.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="unread-only-toggle">
          <input type="checkbox" checked={unreadOnly} onChange={(e)=>setUnreadOnly(e.target.checked)} className="accent-[#002FA7]" />
          Non lus
        </label>
        <div className="flex-1 relative min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search} onChange={(e)=>setSearch(e.target.value)}
            onKeyDown={(e)=>e.key==="Enter" && load()}
            data-testid="search-input"
            placeholder="Rechercher (expéditeur, sujet, tag…)"
            className="w-full pl-10 h-9 border border-gray-300 focus:border-[#002FA7] outline-none text-sm"
          />
        </div>
        <button onClick={load} data-testid="search-btn"
          className="px-4 py-2 bg-white border border-gray-300 hover:border-gray-900 text-sm font-medium">
          Filtrer
        </button>
      </div>

      <div className="bg-white border border-gray-200">
        {loading ? (
          <div className="p-10 text-center text-gray-500 overline">Chargement…</div>
        ) : emails.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            Aucun email dans cette vue. Lancez une synchronisation pour récupérer les derniers emails.
          </div>
        ) : (
          <ul className="divide-y divide-gray-200" data-testid="emails-list">
            {emails.map((em) => {
              const cat = AI_CATEGORIES[em.ai_category] || AI_CATEGORIES.administrative;
              return (
                <li key={em.id} onClick={() => openEmail(em)}
                  data-testid={`email-row-${em.id}`}
                  className="px-5 py-3.5 hover:bg-gray-50 cursor-pointer transition-colors flex items-center gap-3">
                  <span className={`inline-flex shrink-0 items-center px-2 py-0.5 text-xs font-semibold border uppercase tracking-wider ${cat.className}`}>
                    {cat.label}
                  </span>
                  {em.is_priority ? (
                    <span className="text-[10px] font-bold text-[#FF3B30] uppercase tracking-wider shrink-0">PRIO</span>
                  ) : em.is_hidden ? (
                    <EyeOff className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  ) : (
                    <Eye className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className={`text-sm truncate ${em.is_read ? "text-gray-600" : "font-semibold text-gray-900"}`}>{em.from_name || em.from_email}</div>
                      {!em.is_read && <div className="w-1.5 h-1.5 rounded-full bg-[#FF3B30] shrink-0" />}
                      {em.tags && em.tags.length > 0 && (
                        <div className="flex gap-1 shrink-0">
                          {em.tags.slice(0, 3).map(t => (
                            <span key={t} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-700 border border-gray-200 uppercase">
                              <Tag className="w-2.5 h-2.5" /> {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={`text-sm truncate ${em.is_read ? "text-gray-500" : "text-gray-800"}`}>{em.subject}</div>
                    {em.ai_summary && (
                      <div className="text-xs text-gray-500 truncate mt-0.5 italic">→ {em.ai_summary}</div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 shrink-0 font-mono">
                    {new Date(em.received_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o)=>!o && setSelected(null)}>
        <DialogContent className="max-w-3xl rounded-none border border-gray-200 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl pr-8" style={{fontFamily:"'Work Sans', sans-serif"}}>
              {selected?.subject}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 border-b border-gray-200 pb-3">
                <div><span className="overline mr-2">De</span>{selected.from_name} &lt;{selected.from_email}&gt;</div>
                <div className="mt-1"><span className="overline mr-2">Reçu</span>{new Date(selected.received_at).toLocaleString("fr-FR")}</div>
              </div>

              {selected.ai_summary && (
                <div className="bg-blue-50 border border-blue-200 p-4">
                  <div className="overline text-[#002FA7] mb-2">// Résumé IA</div>
                  <div className="text-sm text-gray-800">{selected.ai_summary}</div>
                  {selected.ai_action && (
                    <div className="text-sm text-gray-900 mt-2"><strong>Action :</strong> {selected.ai_action}</div>
                  )}
                  <div className="text-xs text-gray-500 mt-2">
                    Urgence : {selected.ai_urgency}/5 • Catégorie actuelle : <strong>{selected.ai_category}</strong>
                    {selected.user_category && <span className="ml-1">(corrigée par vous)</span>}
                  </div>
                </div>
              )}

              {/* Category correction (auto-learning) */}
              <div className="bg-white border border-gray-200 p-4" data-testid="category-correction">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-[#002FA7]" />
                  <div className="overline">// Corriger la catégorie (auto-apprentissage IA)</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(AI_CATEGORIES).map(([key, cat]) => (
                    <button key={key} onClick={() => correctCategory(key)}
                      data-testid={`correct-cat-${key}`}
                      className={`inline-flex items-center px-2 py-1 text-xs font-semibold border uppercase tracking-wider transition-opacity hover:opacity-80 ${cat.className} ${selected.ai_category === key ? "ring-2 ring-offset-1 ring-[#002FA7]" : ""}`}>
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div className="bg-white border border-gray-200 p-4" data-testid="tags-section">
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="w-4 h-4 text-gray-700" />
                  <div className="overline">// Tags personnalisés</div>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {(selected.tags || []).map(t => (
                    <span key={t} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                      <Tag className="w-3 h-3" /> {t}
                      <button onClick={() => removeTag(t)} data-testid={`tag-remove-${t}`} className="ml-1 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {(selected.tags || []).length === 0 && <span className="text-xs text-gray-500">Aucun tag.</span>}
                </div>
                <div className="flex gap-2">
                  <input value={tagInput} onChange={(e)=>setTagInput(e.target.value)}
                    onKeyDown={(e)=>{ if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                    data-testid="tag-input"
                    placeholder="Ajouter un tag (ex: client-x, contrat…)"
                    className="flex-1 h-9 px-3 border border-gray-300 focus:border-[#002FA7] outline-none text-sm" />
                  <button onClick={addTag} data-testid="tag-add-btn"
                    className="px-3 py-1.5 bg-[#002FA7] hover:bg-[#002277] text-white text-xs font-medium uppercase tracking-wider">
                    Ajouter
                  </button>
                </div>
              </div>

              <div>
                <div className="overline mb-2">// Contenu</div>
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans bg-gray-50 border border-gray-200 p-4 max-h-96 overflow-y-auto">
                  {selected.body || "(vide)"}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
